<template>
  <div
    class="loading-spinner"
    :class="[containerClass, { 'is-fullscreen-overlay': fullscreen }]"
    v-show="shouldShow"
  >
    <div class="has-text-centered" :class="spinnerContainerClass">
      <BrandLoader :size="size" />
    </div>
    <div v-if="title" class="loading-label">{{ title }}</div>
    <p v-if="subtitle" class="loading-sublabel">{{ subtitle }}</p>
  </div>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from "vue";
import BrandLoader from "./BrandLoader.vue";

const props = defineProps({
  title: {
    type: String,
    default: "Loading...",
  },
  subtitle: {
    type: String,
    default: "",
  },
  // Deprecated. The loader is always the brandmark now; kept so existing call
  // sites that pass it don't warn.
  icon: {
    type: [String, Array],
    default: () => ["fas", "spinner"],
  },
  size: {
    type: [String, Number],
    default: "3x",
  },
  centered: {
    type: Boolean,
    default: true,
  },
  titleSize: {
    type: String,
    default: "is-4",
  },
  subtitleSize: {
    type: String,
    default: "is-6",
  },
  // Deprecated alongside `icon` — the mark carries the brand's own colors.
  spinnerColor: {
    type: String,
    default: "has-text-primary",
  },
  containerClass: {
    type: String,
    default: "",
  },
  spacing: {
    type: String,
    default: "mb-6",
  },
  // New prop for fullscreen overlay mode
  fullscreen: {
    type: Boolean,
    default: false,
  },
  // New prop for minimum display time in milliseconds
  minDisplayTime: {
    type: Number,
    default: 3000, // 3 seconds
  },
  // New prop to control visibility externally
  show: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:show"]);

// Internal state for minimum display time
const startTime = ref(null);
const shouldShow = ref(false);
const minDisplayTimer = ref(null);

// `spacing` is honoured only when there is no label to set the rhythm.
const spinnerContainerClass = computed(() => (props.title ? [] : [props.spacing]));

const containerClass = computed(() => [
  props.centered ? "has-text-centered" : "",
  props.containerClass,
]);

// Function to start the minimum display timer
const startMinDisplayTimer = () => {
  if (props.minDisplayTime > 0) {
    startTime.value = Date.now();
    shouldShow.value = true;
  }
};

// Function to stop the minimum display timer
const stopMinDisplayTimer = () => {
  if (minDisplayTimer.value) {
    clearTimeout(minDisplayTimer.value);
    minDisplayTimer.value = null;
  }
};

// Function to handle hiding with minimum display time
const handleHide = () => {
  if (props.minDisplayTime > 0 && startTime.value) {
    const elapsed = Date.now() - startTime.value;
    const remaining = Math.max(0, props.minDisplayTime - elapsed);

    if (remaining > 0) {
      // Wait for the remaining time before hiding
      minDisplayTimer.value = setTimeout(() => {
        shouldShow.value = false;
        startTime.value = null;
        emit("update:show", false);
      }, remaining);
    } else {
      // Minimum time has passed, hide immediately
      shouldShow.value = false;
      startTime.value = null;
      emit("update:show", false);
    }
  } else {
    // No minimum display time, hide immediately
    shouldShow.value = false;
    emit("update:show", false);
  }
};

// Watch for show prop changes
watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      startMinDisplayTimer();
    } else {
      handleHide();
    }
  },
  { immediate: true }
);

// Cleanup on unmount
onUnmounted(() => {
  stopMinDisplayTimer();
});
</script>

<style scoped>
/* Label sits under the mark: small, uppercase, tracked out — it is a status,
   not a headline. */
.loading-label {
  margin-top: 0.75rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #8b90a0;
}

.loading-sublabel {
  margin-top: 0.35rem;
  font-size: 0.8125rem;
  color: #a2a7b4;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Fullscreen overlay styles */
.loading-spinner.is-fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(2px);
  z-index: 10003; /* Higher than any other element in the app */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}


</style>
